import { TrackedChange } from 'models/changes'
import { MutableRefObject, useCallback } from 'react'
import { ScrollToContent } from '../store/scrollStore'
import { SCROLL_CONFIG } from '../utils/TreeNodesUtils'

interface SetPendingScrollRequest {
    scrollToId: string
    correspondingTrackChange: TrackedChange
    timestamp: number
}

export interface ScrollRequestExecutorParams {
    scrollToContent: ScrollToContent
    handleTrackChangeFromActiveTabRef: MutableRefObject<
        (trackChangeTab: string) => Promise<boolean>
    >
    handleLatestContentItemRef: MutableRefObject<
        (trackChange: TrackedChange) => Promise<boolean>
    >
    scrollToElementRef: MutableRefObject<(id: string) => Promise<void>>
    clearScrollToContentRef: MutableRefObject<() => void>
    setPendingScrollRequest: (req: SetPendingScrollRequest) => void
    isHandlingChangeTab: boolean
    safeDelay: (ms: number) => Promise<boolean>
    isCurrentRequest: (requestId: string) => boolean
}

/**
 * Hook responsible for executing scroll request operations
 * Single Responsibility: Scroll request execution logic
 * @param {ScrollRequestExecutorParams} params - Parameters for the scroll request executor
 * @returns {Object} - Contains the executeScrollRequest function
 */
export const useScrollRequestExecutor = ({
    handleTrackChangeFromActiveTabRef,
    handleLatestContentItemRef,
    scrollToElementRef,
    clearScrollToContentRef,
    setPendingScrollRequest,
    isHandlingChangeTab,
    safeDelay,
    isCurrentRequest,
}: ScrollRequestExecutorParams) => {
    const executeScrollRequest = useCallback(
        async (
            requestId: string,
            scrollToId: string,
            correspondingTrackChange: TrackedChange
        ): Promise<void> => {
            try {
                // Step 1: Handle tab switching
                const tabSwitchResult = await handleTrackChangeFromActiveTabRef.current(
                    correspondingTrackChange.partType
                )
                const isArticleLevel =
                    correspondingTrackChange.contentItemId ===
                    correspondingTrackChange.rootContentItemId
                if (!isCurrentRequest(requestId)) return

                if (!tabSwitchResult) {
                    clearScrollToContentRef.current()
                    return
                }

                // Step 2: Wait for tab switch if needed
                if (isHandlingChangeTab && !isArticleLevel) {
                    const isStillMounted = await safeDelay(
                        SCROLL_CONFIG.DELAYS.TAB_SWITCH
                    )

                    if (!isCurrentRequest(requestId) || !isStillMounted) return
                }

                if (!isCurrentRequest(requestId)) return

                // Step 3: Handle content item
                const hasContentItemElement = await handleLatestContentItemRef.current(
                    correspondingTrackChange
                )

                if (!isCurrentRequest(requestId)) return

                // Step 4: Scroll or set pending request
                if (hasContentItemElement) {
                    await scrollToElementRef.current(scrollToId)
                    clearScrollToContentRef.current()
                } else {
                    setPendingScrollRequest({
                        scrollToId,
                        correspondingTrackChange,
                        timestamp: Date.now(),
                    })
                    clearScrollToContentRef.current()
                }
            } catch (error) {
                console.error('Error handling scroll request:', error)
                if (isCurrentRequest(requestId)) {
                    clearScrollToContentRef.current()
                }
            }
        },
        [
            setPendingScrollRequest,
            isHandlingChangeTab,
            safeDelay,
            isCurrentRequest,
        ]
    )

    return { executeScrollRequest }
}
